import type { DropdownOption } from "@/redistribution/models/DropdownOption.js";

/* ── Warehouse Options Builder ────────────────────────────────────────────── */

function normalizeWarehouseLabel(label: string): string {
  return label.replace(/^Warehouse\s+/i, "").trim();
}

interface WarehouseResponseRow {
  warehouseID: number;
  warehouseName: string;
  division: string;
}

export function buildWarehouseOptions(
  rows: WarehouseResponseRow[]
): DropdownOption[] {
  const dedupedRows = [
    ...new Map(
      rows
        .filter(
          (row) =>
            row.warehouseID.toString().trim() !== "" &&
            row.warehouseName.trim() !== ""
        )
        .map((row) => [`${row.division}_${row.warehouseID.toString()}`, row])
    ).values(),
  ];

  const duplicateCounts = new Map<string, number>();
  for (const row of dedupedRows) {
    const normalizedLabel = normalizeWarehouseLabel(row.warehouseName);
    duplicateCounts.set(
      normalizedLabel,
      (duplicateCounts.get(normalizedLabel) ?? 0) + 1
    );
  }

  return dedupedRows.map((row) => {
    const normalizedLabel = normalizeWarehouseLabel(row.warehouseName);
    const hasDuplicateLabel = (duplicateCounts.get(normalizedLabel) ?? 0) > 1;
    const label = hasDuplicateLabel
      ? `${normalizedLabel} (${row.division})`
      : normalizedLabel;

    return {
      label,
      value: `${row.division}_${row.warehouseID.toString()}`,
      metadata: { division: row.division, warehouseID: row.warehouseID },
    } satisfies DropdownOption;
  });
}

/* ── Vendor Options Builder ────────────────────────────────────────────────── */

interface VendorResponseRow {
  vendorCode: string;
  vendorName: string;
}

export function buildVendorOptions(
  rows: VendorResponseRow[]
): DropdownOption[] {
  const options: DropdownOption[] = rows
    .filter((row) => row.vendorCode && row.vendorCode.trim() !== "")
    .map((row) => ({ label: row.vendorCode, value: row.vendorCode }));
  return options.toSorted((a, b) => a.value.localeCompare(b.value));
}

/* ── Collection Options Builder ──────────────────────────────────────────── */

interface CollectionResponseRow {
  vendorId: string;
  division: string;
  theme: string;
}

export interface CollectionResponse {
  response: CollectionResponseRow[];
}

export function mapCollectionResponseToOptions(
  data: CollectionResponse
): DropdownOption[] {
  return data.response
    .filter(
      (row) =>
        row.vendorId.trim() !== "" &&
        row.theme.trim() !== "" &&
        row.division.trim() !== ""
    )
    .toSorted((a, b) => {
      const vendorComparison = a.vendorId.localeCompare(b.vendorId);
      if (vendorComparison !== 0) {
        return vendorComparison;
      }
      return a.theme.localeCompare(b.theme);
    })
    .map((row) => ({
      label: row.theme,
      value: row.theme,
      metadata: { division: row.division, vendorId: row.vendorId },
    }));
}

/* ── Collections Cache ───────────────────────────────────────────────────── */

type CollectionsCache = Record<string, DropdownOption[]>;

const COLLECTIONS_CACHE_KEY = "redisku.collections.cache.v2";

export function getCollectionsCache(): CollectionsCache {
  try {
    const cachedValue = globalThis.localStorage.getItem(COLLECTIONS_CACHE_KEY);
    if (!cachedValue) {
      return {};
    }
    const parsed = JSON.parse(cachedValue);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
      return {};
    }
    return parsed as CollectionsCache;
  } catch {
    return {};
  }
}

export function setCachedCollections(
  vendorId: string,
  options: DropdownOption[]
): void {
  try {
    const cache = getCollectionsCache();
    cache[vendorId] = options;
    globalThis.localStorage.setItem(
      COLLECTIONS_CACHE_KEY,
      JSON.stringify(cache)
    );
  } catch {
    // Ignore localStorage failures and continue with in-memory results only.
  }
}
